import React, { useEffect } from 'react';
import {
  Navigate,
  useLocation,
  useMatch,
  Outlet,
} from 'react-router-dom';
import { observer } from 'mobx-react-lite';

import { useStore } from '../store/storeHooks';
import { BasicPage } from '../components/layout';
import { Routes } from './routes';

type Props = {
  path?: string;
  keepInHistory?: boolean;
  children?: React.ReactNode;
};

const ONBOARDING_REDIRECT_PATH_KEY = 'routing.redirectAfterOnboardingPath';

export const AuthorizedRoute = observer((props: Props) => {
  const { path, keepInHistory, children } = props;

  const store = useStore();
  const location = useLocation();
  const route = location.pathname + location.search;
  const match = useMatch(path || '');

  const { auth, storage, widget } = store;

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    // Query param used for redirecting to either the signin or
    // signup pages if not authenticated
    const isExistingUser = params.get('existingUser');
    if (isExistingUser === 'false') {
      store.storage.setLocal('user.isNotRegistered', 'true');
    }
  }, [store, location.search]);

  if (widget.isWidgetMode) {
    return <BasicPage
      showLoadMask={true}
      showHeader={false} />;
  }

  if (!auth.isAuthorized) {
    return <BasicPage
      showLoadMask={true}
      showHeader={false} />;
  }

  if (!auth.isOnboarded && Routes.onboard() !== path) {
    storage.setLocal(ONBOARDING_REDIRECT_PATH_KEY, route);
    return <Navigate to={Routes.onboard()} replace />;
  }

  const redirectPath = storage.getLocal(ONBOARDING_REDIRECT_PATH_KEY);

  if (redirectPath && path !== Routes.onboard()) {
    if (route === redirectPath) {
      storage.removeLocal(ONBOARDING_REDIRECT_PATH_KEY);
    } else {
      return <Navigate to={redirectPath} replace />;
    }
  }

  if (path && keepInHistory && match) {
    storage.setLocal(
      'router.lastVisitedLibrary',
      { ...match },
      true
    );
  }

  return <>{children || <Outlet />}</>;
});
